<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList && hasChartData">
        <StaticDataTable class="static-table-list" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{stockValueTableData.nameTitle}}</th>
              <th width="40%">{{stockValueTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of stockValueTableData.data" :key="index">
              <td>
                <div v-if="!stockValueTableData.isStockData || !dataValue.stockId">{{dataValue.name}}</div>
                <LinkStock v-if="stockValueTableData.isStockData && dataValue.stockId" :id="dataValue.stockId" :name="dataValue.name" />
              </td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="stockValueSeries"
          :legend="stockValueLegend"
          :options="stockValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

const StockValueChartTypes = {
  stockValueCategory: 'stockValueCategory',
  stockValueStock: 'stockValueStock',
  stockValueOffcut: 'stockValueOffcut',
  stockValueBlock: 'stockValueBlock',
  stockOnHandCategory: 'stockOnHandCategory',
  stockOnHandStock: 'stockOnHandStock',
  stockOnHandBlock: 'stockOnHandBlock',
  stockOnHandOffcut: 'stockOnHandOffcut',
}

export default {
  name: 'StockValueChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean
  },
  StockValueChartTypes: StockValueChartTypes,
  data() {
    return {
      stockValueSeries: [],
      stockValueTableData: {},
      stockValueLegend: [],
      stockValueOptions: {}
    }
  },
  computed: {
    hasChartData() {
      if(this.stockValueSeries) {
        return this.stockValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    }
  },
  mounted() {
    this.requestChartData()
  },
  beforeDestroy() {
  },
  watch: {
  },
  methods: {
    requestChartData() {
      var summaryFilter = {
        maxGroupSize: 7
      }
      this.$apiService.analyticsService.getStockValueOnHandSummary(summaryFilter)
      .then(response => {
        this.stockValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatCurrency(param.value)} (${param.percent}%)`
          }
        }

        switch(this.type) {
          case StockValueChartTypes.stockValueCategory:
            this.buildChartData({
              name: 'Category Stock Value',
              data: response.data.categoryValueList,
              dataTableName: 'Category',
              dataTableValueName: 'Value',
              isCurrency: true
            })
            break
          case StockValueChartTypes.stockValueStock:
            this.buildChartData({
              name: 'Stock Value',
              data: response.data.stockValueList,
              dataTableName: 'Stock',
              dataTableValueName: 'Value',
              isCurrency: true,
              isStockData: true
            })
            break
          case StockValueChartTypes.stockValueOffcut:
            this.buildChartData({
              name: 'Offcut Value',
              data: response.data.offcutValueList,
              dataTableName: 'Stock',
              dataTableValueName: 'Value',
              isCurrency: true,
              isStockData: true
            })
            break
          case StockValueChartTypes.stockValueBlock:
            this.buildChartData({
              name: 'Block Value',
              data: response.data.blockValueList,
              dataTableName: 'Stock',
              dataTableValueName: 'Value',
              isCurrency: true,
              isStockData: true
            })
            break
          case StockValueChartTypes.stockOnHandCategory:
            this.buildChartData({
              name: 'Category Stock On Hand',
              data: response.data.categoryOnHandList,
              dataTableName: 'Category',
              dataTableValueName: 'Area',
              isSquareMetres: true
            })
            break
          case StockValueChartTypes.stockOnHandStock:
            this.buildChartData({
              name: 'Stock On Hand',
              data: response.data.stockOnHandList,
              dataTableName: 'Stock',
              dataTableValueName: 'Area',
              isSquareMetres: true,
              isStockData: true
            })
            break
          case StockValueChartTypes.stockOnHandBlock:
            this.buildChartData({
              name: 'Block Count',
              data: response.data.blockCountList,
              dataTableName: 'Stock',
              dataTableValueName: 'Block Count',
              isStockData: true
            })
            break
          case StockValueChartTypes.stockOnHandOffcut:
            this.buildChartData({
              name: 'Offcut On Hand',
              data: response.data.offcutOnHandList,
              dataTableName: 'Stock',
              dataTableValueName: 'Area',
              isSquareMetres: true,
              isStockData: true
            })
            break
        }
      })
    },
    buildChartData(options) {
      var displayValueFn = (value) => {
        if(options.isCurrency)
          return Utils.formatCurrency(value)
        if(options.isSquareMetres)
          return Utils.formatDecimal(value, 2, true) + 'm&sup2;'

        return value
      }

      this.stockValueOptions.tooltip = {
        formatter(param) {
          return `${param.seriesName} <br />${param.name} : ${displayValueFn(param.value)} (${param.percent}%)`
        }
      }
      this.stockValueSeries = [{
        name: options.name,
        data: options.data.map(group => {
          return {
            name: group.name,
            value: Utils.roundCurrency(group.value)
          }
        }),
        label: {
          show: true,
          formatter(param) {
            return param.name
          }
        },
      }]
      this.stockValueLegend = {
        data: options.data.map(group => group.name)
      }

      this.stockValueTableData = {
        nameTitle: options.dataTableName,
        valueTitle: options.dataTableValueName,
        isStockData: options.isStockData,
        isStockCategoryData: options.isStockCategoryData,
        data: options.data.map(group => {
          return {
            name: group.name,
            displayValue: displayValueFn(group.value),
            stockId: group.stockId,
            stockCategoryId: group.stockCategoryId
          }
        })
      }
    }
  }
}
</script>